import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchClientsRequest, resetClientsTableData } from '../../../redux/client/clientSlice';
import ClientTable, { CellClickProps } from '../ClientTable';
import CilentsEmpty from './CilentsEmpty';
import { activeClientsColumns } from '../Columns';
import { ClientsProps } from '../../../pages/Clients/Clients';
import createPermissionCheckers from '../../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../../types/commonTypes';
import { SubscriptionPlanTypes } from '../../../types/coach';
import { showBanner } from '../../../utils/paymentUtills';
import PlanInfoPopup from '../../../pages/Clients/components/PlanInfoPopup';

const ActiveClients = ({
  pageSize,
  currentPage,
  skip,
  handleOnPaginationModelChange,
}: ClientsProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [initialRender, setInitialRender] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [exisitingCoach, setExistingCoach] = useState<boolean>(false);
  const { groupid, groupname } = useParams();
  const coach = useAppSelector((state) => state.coach.coach);
  const { clients, totalClientCount, fetchClientsLoading, error } = useAppSelector(
    (state) => state.client
  );
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const { activateFreePlanLoading } = useAppSelector((state) => state.subscription);
  const subscriptionPlanStatus = useAppSelector(
    (state) => state.coach.coach.subscriptionPlanStatus
  );
  const currentPlan = subscriptionPlanStatus?.currentPlan;
  const paymentStatus = subscriptionPlanStatus?.paymentStatus;
  const daysLeft = subscriptionPlanStatus?.daysLeft;
  const clientLimitStatus = useAppSelector((state) => state.coach.coach.clientCountStatus);

  const fetchClients = () => {
    if (coach) {
      const payload = {
        coachId: coach?.id,
        nameText: searchTerm,
        skip,
        take: pageSize,
        isArchived: false,
      };
      dispatch(fetchClientsRequest(payload));
    }
  };

  useEffect(() => {
    if (!initialRender) {
      fetchClients();
    } else {
      setInitialRender(false);
    }
  }, [coach.id, pageSize, currentPage]);

  useEffect(() => {
    if (!initialRender) {
      const debounceSearch = setTimeout(() => {
        fetchClients();
      }, 1000);
      return () => clearTimeout(debounceSearch);
    }
  }, [searchTerm, dispatch]);

  const handleChangeSearchTerm = (newValue: string) => {
    setSearchTerm(newValue);
  };

  const handleOnCellClickActiveClient = (params: CellClickProps) => {
    if (!['actions', 'status'].includes(params.field)) {
      let tab = 'client-details';

      if (checkPermissions(UserPermissionType.NOTE_MODULE_VIEW_NOTES)) {
        tab = 'notes';
      } else if (checkPermissions(UserPermissionType.CHAT_MODULE_VIEW_CHAT)) {
        tab = 'chat';
      } else if (checkPermissions(UserPermissionType.PROGRESS_MODULE_GET_PROGRESS_SUMMARY)) {
        tab = 'progress';
      } else if (checkPermissions(UserPermissionType.ACTIVITY_MODULE_VIEW_ACTIVITIES)) {
        tab = 'activities';
      }

      const path = groupid
        ? `/clients/${groupid}/${groupname}/${params.row.id}?tab=${tab}`
        : `/clients/${params.row.id}?tab=${tab}`;
      navigate(path);
    }
  };

  useEffect(() => {
    if (totalClientCount > 0 && !exisitingCoach) {
      setExistingCoach(true);
    }
  }, [totalClientCount, exisitingCoach]);

  useEffect(
    () => () => {
      dispatch(resetClientsTableData());
    },
    []
  );

  if (
    currentPlan === SubscriptionPlanTypes.NONE &&
    !activateFreePlanLoading &&
    coach.isPrincipalCoach
  ) {
    return <PlanInfoPopup isOpen />;
  }

  return !fetchClientsLoading && !error && !exisitingCoach && totalClientCount === 0 ? (
    <CilentsEmpty
      addExtraBottomMargin={
        clientLimitStatus &&
        paymentStatus &&
        currentPlan &&
        showBanner(clientLimitStatus, paymentStatus, currentPlan, daysLeft)
      }
    />
  ) : (
    <ClientTable
      handleOnCellClick={handleOnCellClickActiveClient}
      rows={!fetchClientsLoading ? clients : []}
      rowCount={totalClientCount}
      loading={fetchClientsLoading}
      columns={activeClientsColumns}
      handleOnPaginationModelChange={handleOnPaginationModelChange}
      handleOnChangeSearchTerm={handleChangeSearchTerm}
      searchTerm={searchTerm}
      pageSize={pageSize}
      page={currentPage}
      addExtraBottomMargin={
        clientLimitStatus &&
        paymentStatus &&
        currentPlan &&
        showBanner(clientLimitStatus, paymentStatus, currentPlan, daysLeft)
      }
    />
  );
};

export default ActiveClients;
